"use client";

import { cn } from "@/lib/utils";
import { track } from "@vercel/analytics";
import { trackEvent } from "@/lib/analytics";
import Link from "next/link";
import type { ComponentProps } from "react";
import { useFormState } from "react-dom";
import { usePathname } from "next/navigation";
import { ArrowRightIcon } from "lucide-react";

import { Input } from "@/components/ui/input";
import { SubmitButton } from "@/components/forms/SubmitButton";
import { subscribe } from "@/app/email/newsletter/actions";
import type { TopicSlug } from "@/lib/email/topics";

type EmailFormProps = {
  link?: string;
  topic: TopicSlug;
} & Omit<ComponentProps<"form">, "action">;

const EmailForm = ({ link, topic, ...rest }: EmailFormProps) => {
  const [state, formAction] = useFormState(subscribe, {});
  const pathname = usePathname();

  return (
    <form action={formAction} className={cn("mt-8", rest.className)}>
      <input type="hidden" name="topic" value={topic} />
      <input type="hidden" name="source" value={pathname} />
      <fieldset className="flex items-center">
        <Input
          name="email"
          type="email"
          autoComplete="email"
          placeholder="Deine E-Mail"
          className="flex-grow bg-transparent rounded-full py-3 px-6 border border-black"
          required
        />
        <SubmitButton
          className="rounded-full aspect-square"
          size="icon"
          onClick={() => {
            track("Newsletter submit");
            // Nur der Klick — Validierungs- und DB-Fehler zaehlen mit. Die
            // echte Anmeldung ist newsletter_signup_created (serverseitig),
            // die Conversion ist newsletter_confirmed nach dem Double-Opt-in.
            trackEvent("newsletter_submit_attempted", { topic });
          }}
        >
          <ArrowRightIcon className="h-6 w-6" />
          <span className="sr-only">Absenden</span>
        </SubmitButton>
      </fieldset>

      <div className="mx-auto flex flex-col gap-4 justify-center text-center">
        {link && (
          <div className="mt-3 text-center text-sm text-gray-600 gap-1">
            Mit Absenden des Formulars stimme ich den{" "}
            <Link href={link} className="underline">
              Datenschutzbestimmungen
            </Link>{" "}
            zu
          </div>
        )}

        <p aria-live="polite" className="sr-only">
          {state?.error}
        </p>
      </div>
    </form>
  );
};

export { EmailForm };
